/**
 * Library + retailer holdings for the Book page's "In World Libraries"
 * section. Hand-curated from the WhatsApp evidence in
 * Resources/Book/Libraries (catalog-record screenshots) and verified
 * online retailer URLs from a 2026-04-27 web search.
 *
 * Each entry's `url` should land users on (or as close as possible to)
 * the actual book record in that catalog. Where a deep link to the
 * specific record isn't reliably constructible (university OPACs vary
 * widely), the link falls back to a search query that returns the
 * book at the top of the results.
 *
 * `screenshot` (when present) points at a public/refs/* image showing
 * the catalog entry as visual proof; rendered next to the link.
 */

export interface Holding {
  /** Stable identifier — used as a React key and a data-attribute. */
  id: string;
  /** Bilingual display name. */
  name: { ar: string; en: string };
  /** Bilingual location/country line shown beneath the name. */
  location: { ar: string; en: string };
  /** Direct URL into the catalog/store. Must resolve to or near the book record. */
  url: string;
  /** Optional public/refs path to a small screenshot of the catalog entry. */
  screenshot?: string;
  /** Optional short note (e.g. "5 holdings"). Bilingual. */
  note?: { ar: string; en: string };
}

// WorldCat OCLC record for the book — useful as a global catalog
// fallback link when a specific library doesn't have a directly
// accessible deep link to the record. Source: WorldCat record
// 1227848566 (provided by the family).
const WORLDCAT_RECORD = "https://www.worldcat.org/oclc/1227848566";

export const LIBRARIES: readonly Holding[] = [
  {
    id: "harvard",
    name: {
      ar: "جامعة هارفارد · فهرس HOLLIS",
      en: "Harvard University · HOLLIS catalog",
    },
    location: { ar: "كامبريدج، الولايات المتحدة الأمريكية", en: "Cambridge, USA" },
    // Direct link to the catalog record (alma id 990143609790203941) —
    // verified by the family.
    url: "https://hollis.harvard.edu/discovery/fulldisplay?docid=alma990143609790203941&context=L&vid=01HVD_INST:HVD2&lang=en&search_scope=MyInst_and_CI&adaptor=Local%20Search%20Engine&tab=Everything&query=any,contains,%D8%A7%D9%84%D8%AD%D8%B6%D8%A7%D8%B1%D8%A9%20%D8%A7%D9%84%D9%85%D8%B5%D8%B1%D9%8A%D8%A9%20%D8%A7%D9%84%D9%82%D8%AF%D9%8A%D9%85%D8%A9%20%D8%B1%D8%A4%D9%8A%D8%A9%20%D8%B9%D9%82%D9%84%D9%8A%D8%A9%20%D9%85%D9%88%D8%AB%D9%82%D8%A9%20%D9%86%D8%A8%D9%8A%D9%84%20%D8%AA%D9%88%D9%81%D9%8A%D9%82&offset=0",
    screenshot: "/refs/lib-7.jpg",
  },
  {
    id: "bnf",
    name: {
      ar: "المكتبة الوطنية الفرنسية",
      en: "Bibliothèque nationale de France",
    },
    location: { ar: "باريس، فرنسا", en: "Paris, France" },
    // BNF holding evidenced by WorldCat aggregator; deep link is the
    // shared WorldCat record which lists BNF as a holding library.
    url: WORLDCAT_RECORD,
    screenshot: "/refs/lib-2.jpg",
  },
  {
    id: "squ",
    name: {
      ar: "مكتبة جامعة السلطان قابوس",
      en: "Sultan Qaboos University Library",
    },
    location: { ar: "مسقط، سلطنة عُمان", en: "Muscat, Oman" },
    // SQU's WorldCat-branded record for the book (OCLC 1227848566) —
    // verified by the family.
    url: "https://squ.on.worldcat.org/oclc/1227848566",
    screenshot: "/refs/lib-1.jpg",
  },
  {
    id: "abudhabi",
    name: {
      ar: "دائرة الثقافة والسياحة — أبوظبي",
      en: "Department of Culture and Tourism — Abu Dhabi",
    },
    location: { ar: "أبوظبي، الإمارات العربية المتحدة", en: "Abu Dhabi, UAE" },
    // Multi-search query URL that lands on the catalog results for
    // the book — verified by the family.
    url: "https://library.dctabudhabi.ae/multisearch?bentoq=%D8%A7%D9%84%D8%AD%D8%B6%D8%A7%D8%B1%D8%A9+%D8%A7%D9%84%D9%85%D8%B5%D8%B1%D9%8A%D8%A9+%D8%A7%D9%84%D9%82%D8%AF%D9%8A%D9%85%D8%A9+%D8%B1%D8%A4%D9%8A%D8%A9+%D8%B9%D9%82%D9%84%D9%8A%D8%A9+%D9%85%D9%88%D8%AB%D9%82%D8%A9+%D9%86%D8%A8%D9%8A%D9%84+%D8%AA%D9%88%D9%81%D9%8A%D9%82&catalog=bento",
    screenshot: "/refs/lib-3.jpg",
    note: { ar: "خمس نسخ متاحة", en: "5 holdings" },
  },
  {
    id: "kapl",
    name: {
      ar: "مكتبة الملك عبد العزيز العامة",
      en: "King Abdulaziz Public Library",
    },
    location: { ar: "الرياض، المملكة العربية السعودية", en: "Riyadh, Saudi Arabia" },
    // No deep link to the KAPL record was supplied; falling back to
    // the WorldCat aggregator record which lists KAPL among the
    // holdings.
    url: WORLDCAT_RECORD,
    screenshot: "/refs/lib-5.jpg",
  },
];

// All retailers removed 2026-04-28 — every public listing the family
// found (EgyBookstore, Neelwafurat, Google Books catalog,
// Noor Library) was posting stale 2015-printing pricing in EGP, which
// would mislead any buyer clicking through. Until a current listing is
// available, the Book page hides the "Buy the book" section entirely
// and routes buyers through the on-page Order CTA → /contact form for
// direct family fulfilment.
//
// To reinstate when a current listing exists: add the entry back here;
// Book.tsx already conditionally renders the section when this array
// is non-empty, so no UI change is required.
export const RETAILERS: readonly Holding[] = [];
